// DOCS → MATERIALS — the raw input: what people have ASKED the project for.
// (What it already DOES is read from its apps — see backend app-implementation.)
//
// Every uploaded file and every typed request, versioned AS A SET: any change
// mints the next version, and a past version renders exactly as it was (the
// backend snapshots each version's items rather than recomputing them).
//
// The version picker and the between-versions diff are the point of the tab:
// "what did we know when we built this" has to be answerable, and "what
// changed" is what the project is BUILT from.
//
// HEURISTIC FIRST (2026-09-03). Minting a version files ONE project-assistant
// build card on the Board (kind change_project, brief = the changed sources,
// the original file attached) — the same single-session path "New project from
// a brief" takes. It does NOT book a requirements review any more: the analysis
// (requirements → tasks → tests → evidence) is optional, offered on the
// Requirements tab once that build has landed, so it starts from what was built.
//
// FILES COME IN HERE. There are exactly two ways a material is created — attach
// a file, or type a request — and both live on this tab. Attachment reuses the
// requirement intake machinery end to end rather than growing a second upload
// path: presigned PUT to the browser-facing S3 endpoint, then registration
// through POST /api/docs/materials, which classifies the file and reads it
// server-side (docx → text, audio → the transcriber). Nothing is parsed here.
//
// ONE DROP IS ONE VERSION. Files land as `parsing` rows; the version is minted
// only once every file in the batch has its words, so a drop of three documents
// costs one version and one build card instead of three of each.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, Plus, RefreshCw, FileText, MessagesSquare, Mic, ChevronDown, ChevronRight,
  History, Check, Pencil, EyeOff, Paperclip, UploadCloud, Download, Image as ImageIcon, ListChecks, X, GitCompareArrows,
} from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";
import BuildPreviewPanel from "./BuildPreviewPanel";
import { uploadOriginal } from "./upload";

const KIND_ICON = { doc: FileText, text: MessagesSquare, audio: Mic, pdf: FileText, md: FileText, image: ImageIcon, meeting: Mic, slack: MessagesSquare };
const kindIcon = (k) => KIND_ICON[k] || FileText;

// Statuses that mean "the words are not in yet" — the same set the requirement
// intake polls on, because it is the same pipeline filling them in.
const WAITING = new Set(["parsing", "transcribing"]);

export default function MaterialsTab({ projectSlug, jobs, onJobsChange, focusId, onFocusHandled, onOpenFeature, onOpenTask, onCompareRequirements }) {
  const [data, setData] = useState(null);
  const [version, setVersion] = useState(null);      // null = current
  const [diffTo, setDiffTo] = useState(null);
  const [diffFrom, setDiffFrom] = useState(null);
  const [diff, setDiff] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [full, setFull] = useState({});              // id -> full text
  const [coverage, setCoverage] = useState({});      // id -> { segments, gaps, covered_chars, uncovered_chars, total_chars, complete }
  const [covering, setCovering] = useState(false);   // "Cover the rest" booked, waiting for the 202
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", text: "" });
  // Attachment: what we can read (served, not hard-coded), what is being
  // uploaded right now, what is still being read, and whether a drag is over us.
  const [accept, setAccept] = useState("");
  const [supported, setSupported] = useState("");
  const [uploading, setUploading] = useState("");
  const [pending, setPending] = useState([]);   // [{ id, name, status, progress, chars, error }]
  const [dragOver, setDragOver] = useState(false);
  const [dlBusy, setDlBusy] = useState(null);   // material id being downloaded
  const fileInput = useRef(null);
  const dragDepth = useRef(0);

  // Download the ORIGINAL file. The bytes come through the authenticated
  // backend route (a presigned URL can name a host only the backend reaches),
  // so: fetch → blob → a[download] click.
  const downloadOriginal = useCallback(async (i) => {
    setDlBusy(i.id); setErr("");
    try {
      const blob = await fetchData(`docs/materials/${i.id}/file`, { method: "GET", responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = i.name || "material";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      setErr(String(e?.message || `Could not download "${i.name}".`));
    } finally { setDlBusy(null); }
  }, []);

  const load = useCallback(async (v) => {
    setErr("");
    try {
      const q = v ? `?version=${v}` : "";
      const r = await fetchData(`docs/materials${q}`, { method: "GET" });
      setData(r);
    } catch (e) {
      setData({ items: [], versions: [] });
      setErr(String(e?.message || "Could not read the materials."));
    }
  }, []);
  useEffect(() => { setData(null); load(version); }, [projectSlug, version, load]);

  // A review in flight means a version was just minted — re-read when it lands.
  const running = (jobs || []).filter((j) => j.kind === "matrix" && j.status === "running");
  const prevRunning = React.useRef(0);
  useEffect(() => {
    if (prevRunning.current > 0 && running.length < prevRunning.current) load(version);
    prevRunning.current = running.length;
  }, [running.length, load, version]);

  // Deep link from a feature's origin: open that item and show its text.
  useEffect(() => {
    if (!focusId || !data) return;
    setOpen((o) => new Set(o).add(Number(focusId)));
    showFull(Number(focusId));
    onFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, data]);

  const showFull = async (id) => {
    if (full[id] == null) {
      try {
        const q = version ? `?version=${version}` : "";
        const r = await fetchData(`docs/materials/${id}${q}`, { method: "GET" });
        setFull((f) => ({ ...f, [id]: String(r?.text || "") }));
      } catch { setFull((f) => ({ ...f, [id]: "" })); }
    }
    // Coverage — which phrases the requirements actually used. Live view only:
    // the spans are re-found against the live text, and a past version renders
    // its own snapshot.
    if (!version && coverage[id] == null) {
      try {
        const c = await fetchData(`docs/materials/${id}/coverage`, { method: "GET" });
        setCoverage((m) => ({ ...m, [id]: c || null }));
      } catch { setCoverage((m) => ({ ...m, [id]: null })); }
    }
  };

  // After the assistant answered a question about a passage of this material,
  // the highlighted coverage is re-read — the rows it added quote this text.
  const refreshCoverage = async (id) => {
    if (version) return;
    try {
      const c = await fetchData(`docs/materials/${id}/coverage`, { method: "GET" });
      setCoverage((m) => ({ ...m, [id]: c || null }));
    } catch { /* the old highlight stays */ }
  };

  // COVER THE REST: book the review shape that runs ONLY the coverage rounds —
  // every passage no requirement links yet is attached to an existing row, a
  // new row, or a "not a requirement" row. Same gate and one-at-a-time rule as
  // Analyse; a refusal (409) is shown as the reason.
  const coverRest = async () => {
    setCovering(true); setErr(""); setMsg("");
    try {
      const r = await fetchData("docs/features/review", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "coverage" }),
      });
      setMsg(r?.already_running ? "A requirements review is already running — the coverage follows it." : "Linking every unlinked passage of the sources — watch the strip above.");
      onJobsChange?.();
    } catch (e) {
      setErr(String(e?.message || "The coverage could not be booked."));
    } finally { setCovering(false); }
  };
  // When a review FINISHES the highlighted coverage of every open source is
  // stale: the rows it wrote quote this text. Re-read them.
  const doneReviews = (jobs || []).filter((j) => j.kind === "matrix" && j.status === "done").map((j) => j.id).join(",");
  useEffect(() => {
    if (!doneReviews) return;
    for (const id of open) refreshCoverage(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneReviews]);

  const toggle = (id) => setOpen((o) => {
    const n = new Set(o);
    if (n.has(id)) n.delete(id); else { n.add(id); showFull(id); }
    return n;
  });

  // What the commit answered: the build card it filed (or why it could not).
  const builtMsg = (prefix, c) => {
    const h = c?.heuristic;
    if (c?.preview?.state === "generating") return `${prefix} — designing build previews from what changed; approve one below to start the build.`;
    if (h?.request_id) return `${prefix} — build #${h.request_id} filed to the Board; the requirements analysis opens when it is done.`;
    if (h?.error) return `${prefix} — no build card was filed (${h.error}). You can analyse the requirements from the Requirements tab.`;
    if (c && c.changed === false) return `${prefix} — nothing changed, so no new version.`;
    return `${prefix}.`;
  };

  const addRequest = async () => {
    const name = draft.name.trim() || "Typed request";
    const text = draft.text.trim();
    if (!text) { setMsg("A request needs some text."); return; }
    setBusy(true); setMsg("");
    try {
      await fetchData("docs/materials", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ kind: "text", name, text }] }),
      });
      const c = await fetchData("docs/materials/commit", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ summary: `added "${name}"` }),
      });
      setDraft({ name: "", text: "" }); setAdding(false);
      setMsg(builtMsg("Added", c));
      setVersion(null);
      await load(null);
      onJobsChange?.();
    } catch (e) {
      setMsg(String(e?.message || "Could not add the request."));
    } finally { setBusy(false); }
  };

  // ── ATTACHING FILES ──────────────────────────────────────────────────────

  // What we can read is served, not duplicated here: adding a format is a
  // backend change and this tab picks it up with no release.
  useEffect(() => {
    let alive = true;
    fetchData("requirements/source_kinds", { method: "GET" })
      .then((r) => { if (!alive) return; setAccept(r?.accept || ""); setSupported(r?.supported || ""); })
      .catch(() => { if (alive) setSupported("documents, text and audio recordings"); });
    return () => { alive = false; };
  }, []);

  // Refuse an unsupported file INSTANTLY, before uploading it. The backend
  // checks again — it is the authority — but a person who picked the wrong file
  // should not wait for a round trip to hear about it.
  const unsupported = useCallback((file) => {
    if (!accept) return null;   // registry not loaded — let the server decide
    const ext = (String(file.name).toLowerCase().match(/\.[a-z0-9]+$/) || [""])[0];
    const allowed = accept.split(",").map((x) => x.trim()).filter(Boolean);
    if (ext && allowed.includes(ext)) return null;
    return `"${file.name}" is not a format we can read. Supported: ${supported || allowed.join(", ")}.`;
  }, [accept, supported]);

  // Poll whatever is still being read or transcribed, then mint ONE version for
  // the whole batch — that is what makes a three-file drop cost one build card
  // instead of three.
  //
  // The poller's lifetime is the BATCH's, not the waiting subset's. Keying the
  // effect on "the ids still parsing" looks natural and is a trap: the tick that
  // sees the last file turn `ready` also empties that key, so React tears the
  // effect down — and the commit, which runs after the state update in the same
  // tick, is cancelled by its own success. The batch id changes only when a new
  // batch starts, so the settle always gets to run.
  const batch = useRef([]);
  const batchId = pending.length ? pending[0].id : 0;
  useEffect(() => {
    if (!batchId) return undefined;
    let alive = true;
    const settle = async (rows) => {
      const ok = rows.filter((p) => p.status === "ready");
      const failed = rows.filter((p) => p.status === "failed");
      try {
        // A failed file simply is not in the snapshot (the backend only
        // snapshots `ready` rows), so a batch where one document was unreadable
        // still lands the ones that were.
        let c = null;
        if (ok.length) {
          c = await fetchData("docs/materials/commit", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              summary: ok.length === 1 ? `attached "${ok[0].name}"` : `attached ${ok.length} files`,
            }),
          });
        }
        batch.current = [];
        setPending([]);
        setMsg(failed.length
          ? builtMsg(`${ok.length} attached; ${failed.length} could not be read (${failed.map((f) => f.name).join(", ")})`, c)
          : builtMsg("Attached", c));
        setVersion(null);
        await load(null);
        onJobsChange?.();
      } catch (e) {
        batch.current = [];
        setPending([]);
        setMsg(`Attached, but the version could not be minted: ${String(e?.message || e)}`);
        await load(null);
      }
    };
    const tick = async () => {
      const ids = (batch.current || []).map((p) => p.id);
      if (!ids.length) return;
      let fresh;
      try {
        const r = await fetchData(`requirements/sources?ids=${ids.join(",")}`, { method: "GET" });
        fresh = new Map((r?.sources || []).map((s) => [s.id, s]));
      } catch { return; }             // transient — the next tick retries
      if (!alive) return;
      const next = batch.current.map((p) => {
        const f = fresh.get(p.id);
        return f ? { ...p, kind: f.kind, status: f.status, progress: f.progress, chars: f.chars, error: f.error } : p;
      });
      batch.current = next;
      setPending(next);
      if (next.some((p) => WAITING.has(p.status))) return;
      alive = false;                  // settled: stop polling, then commit ONCE
      await settle(next);
    };
    const t = setInterval(tick, 1500);
    tick();
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  // Attach anything supported. Bytes upload, the row is registered, and the
  // BACKEND decides what the file is and reads it. Nothing here parses.
  const attachFiles = useCallback(async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setErr(""); setMsg("");
    const registered = [];
    for (const f of list) {
      const why = unsupported(f);
      if (why) { setErr(why); continue; }
      try {
        setUploading(f.name);
        const key = await uploadOriginal(f, projectSlug);
        const r = await fetchData("docs/materials", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [{ name: f.name, s3_key: key, content_type: f.type || null, bytes: f.size }],
          }),
        });
        const row = (r?.sources || [])[0];
        if (row?.id) {
          registered.push({
            id: row.id, name: f.name, kind: row.kind,
            status: row.status || "parsing", progress: 0, chars: row.chars || 0,
          });
        }
      } catch (ex) {
        // Name the FILE. "fetch failed" on its own leaves people staring at a
        // list with no idea which attachment, or which step, went wrong.
        setErr(`Could not attach "${f.name}": ${String(ex?.message || ex)}`);
      } finally {
        setUploading("");
      }
    }
    if (!registered.length) return;
    // Text files come back `ready` immediately; the poller settles the batch and
    // commits either way, so both paths mint through the same code. The ref is
    // the poller's copy — it reads the batch without re-subscribing to it.
    const next = [...(batch.current || []), ...registered];
    batch.current = next;
    setPending(next);
    await load(version);
  }, [projectSlug, unsupported, load, version]);

  const onPick = (e) => {
    // Snapshot BEFORE clearing the input — FileList is live in Chrome 148+.
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    attachFiles(files);
  };

  // Drag and drop over the whole tab. Depth-counted because dragenter/dragleave
  // fire for every child element the pointer crosses.
  const onDragEnter = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault(); dragDepth.current += 1; setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault(); e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragOver(false);
  };
  const onDrop = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault(); dragDepth.current = 0; setDragOver(false);
    attachFiles(e.dataTransfer?.files);
  };

  const editItem = async (id, patch, why) => {
    setBusy(true); setMsg("");
    try {
      await fetchData(`docs/materials/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const c = await fetchData("docs/materials/commit", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ summary: why }),
      });
      setMsg(builtMsg("Saved", c));
      setVersion(null);
      setFull((f) => { const n = { ...f }; delete n[id]; return n; });
      await load(null);
      onJobsChange?.();
    } catch (e) {
      setMsg(String(e?.message || "Could not save."));
    } finally { setBusy(false); }
  };

  const loadDiff = async (to) => {
    if (diffTo === to) { setDiffTo(null); setDiff(null); return; }
    setDiffTo(to); setDiff(null);
    try {
      const from = diffFrom != null && diffFrom !== "" ? `&from=${diffFrom}` : "";
      const r = await fetchData(`docs/materials/diff?to=${to}${from}`, { method: "GET" });
      setDiff(r);
    } catch (e) { setErr(String(e?.message || "Could not read the diff.")); }
  };
  useEffect(() => { if (diffTo != null) loadDiff2(diffTo); /* eslint-disable-next-line */ }, [diffFrom]);
  const loadDiff2 = async (to) => {
    try {
      const from = diffFrom != null && diffFrom !== "" ? `&from=${diffFrom}` : "";
      const r = await fetchData(`docs/materials/diff?to=${to}${from}`, { method: "GET" });
      setDiff(r);
    } catch { /* keep the previous diff visible */ }
  };

  const versions = data?.versions || [];
  const items = data?.items || [];
  const cur = data?.current_version || null;
  const viewing = data?.version || null;

  return (
    <div className="relative p-6 max-w-5xl mx-auto space-y-4" data-testid="materials-tab"
      onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {/* The whole tab is the drop target — people aim at the list, not at a
          small dashed rectangle, so the rectangle is the FEEDBACK, not the zone. */}
      {dragOver && (
        <div data-testid="materials-dropzone"
          className="absolute inset-3 z-20 pointer-events-none rounded-xl border-2 border-dashed border-nav-active bg-nav-active/10 flex items-center justify-center">
          <div className="inline-flex items-center gap-2 text-sm font-medium text-nav-active">
            <UploadCloud size={18} /> Drop to attach — {supported || "documents, text and audio"}
          </div>
        </div>
      )}
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          <FileText size={20} /> Requirement sources
        </h1>
        <div className="text-sm text-page-muted mt-1">
          everything people have ASKED <b>{projectSlug}</b> for — the files they
          uploaded and the requests they typed. Versioned as a set: any change
          mints a new version. The FIRST version of a new project drafts a BUILD
          PREVIEW — a few variants of the application to approve, comment on or
          regenerate; approving one files ONE build on the Board. Once the shape
          is settled (an approved preview, or an application that already exists)
          new sources skip the preview and go straight into the requirements
          review, which reworks only what changed. Select any passage and ask
          “Requirements for this?” to check one part of a source. What the project
          already DOES is read from its apps, not from here.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-page-muted">{items.length} item(s)</span>
        {cur ? (
          <label className="inline-flex items-center gap-1 text-xs text-page-muted">
            <History size={12} /> version
            <select data-testid="materials-version" value={viewing ?? ""} onChange={(e) => setVersion(e.target.value ? Number(e.target.value) : null)}
              className="text-xs px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
              {versions.map((v) => (
                <option key={v.version} value={v.version}>v{v.version}{v.version === cur ? " (current)" : ""}</option>
              ))}
            </select>
          </label>
        ) : <span className="text-xs text-page-muted">not versioned yet</span>}
        {!data?.heuristic?.request_id && (!viewing || viewing === cur) && ["generating", "ready", "failed"].includes(data?.preview?.state) ? (
          <span data-testid="materials-preview-state" data-preview-state={data.preview.state}
            className="text-[11px] px-2 py-0.5 rounded-md border border-page-divider text-page-muted">
            {data.preview.state === "generating" ? "build preview · designing…" : data.preview.state === "ready" ? "build preview · approve a variant to build" : "build preview · designer failed"}
          </span>
        ) : null}
        {data?.heuristic?.request_id && (!viewing || viewing === cur) ? (
          <a href={`/p/${encodeURIComponent(projectSlug)}/b/main/board`} data-testid="materials-build"
            data-build-status={data.heuristic.status}
            title={data.heuristic.title || "The build filed from these sources"}
            className="text-[11px] px-2 py-0.5 rounded-md border border-page-divider no-underline text-page-muted hover:text-page-text">
            build #{data.heuristic.request_id} · {["queued", "claimed", "running", "waiting"].includes(data.heuristic.status) ? `${data.heuristic.status} — analysis opens when done` : data.heuristic.status === "done" ? "done — analysis available" : data.heuristic.status}
          </a>
        ) : null}
        {viewing && viewing !== cur ? (
          <span className="text-[11px] px-2 py-0.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300"
            data-testid="materials-reading-old">
            reading v{viewing} of {cur}
          </span>
        ) : null}
        {/* THE REQUIREMENTS DELTA of this sources version: what the analysis
            of these sources added/changed/removed against the previous
            analysed sources version — "without vs with the change request". */}
        {viewing && onCompareRequirements && versions.find((v) => v.version === viewing)?.features_version
          && versions.some((v) => v.version < viewing && v.features_version) ? (
          <button type="button" data-testid="materials-requirements-delta"
            onClick={() => onCompareRequirements({ to: viewing, from: null })}
            title="Open the Requirements tab on the delta between this sources version and the previous analysed one"
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-muted hover:text-page-text">
            <GitCompareArrows size={11} /> Requirements delta of v{viewing}
          </button>
        ) : viewing && versions.find((v) => v.version === viewing) && !versions.find((v) => v.version === viewing)?.features_version && (!viewing || viewing === cur) ? (
          <span className="text-[11px] text-page-muted" data-testid="materials-not-analysed">requirements not derived from v{viewing} yet</span>
        ) : null}
        {viewing ? (
          <button type="button" data-testid="materials-diff-toggle" onClick={() => loadDiff(viewing)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-muted hover:text-page-text">
            {diffTo === viewing ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            {diffTo === viewing ? "Hide changes" : `What changed in v${viewing}`}
          </button>
        ) : null}
        <button type="button" data-testid="materials-attach" onClick={() => fileInput.current?.click()}
          disabled={!!uploading}
          title={`Attach ${supported || "documents, text or audio"} — or drop them anywhere on this page`}
          className="ml-auto inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50">
          <Paperclip size={13} /> Attach files
        </button>
        <input ref={fileInput} type="file" multiple accept={accept || undefined}
          data-testid="materials-file-input" className="hidden" onChange={onPick} />
        <button type="button" data-testid="materials-add" onClick={() => setAdding((a) => !a)}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30">
          <Plus size={13} /> Add a request
        </button>
        <button type="button" data-testid="materials-refresh" onClick={() => load(version)}
          title="Reload" className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
          <RefreshCw size={13} />
        </button>
      </div>

      {/* THE BUILD PREVIEW — the gate before the build (see BuildPreviewPanel):
          variants of the change to approve, comment on or regenerate. Renders
          only while a version has a preview stage; the strip above narrates. */}
      {(!viewing || viewing === cur) ? (
        <BuildPreviewPanel projectSlug={projectSlug} onChanged={() => { load(null); onJobsChange?.(); }} />
      ) : null}

      {/* See FeaturesTab: the page-level ReviewStatusStrip owns this now, and
          says what a running-only banner never could — that a review is queued
          and unclaimed, or that it died and why. */}
      {uploading ? (
        <div className="text-xs text-page-muted inline-flex items-center gap-1.5" data-testid="materials-uploading">
          <Loader2 size={11} className="animate-spin" /> uploading {uploading}…
        </div>
      ) : null}

      {/* Files whose words are not in yet. They are real rows already — this is
          the batch the next version will be minted from, shown while it settles. */}
      {pending.length > 0 && (
        <div className="space-y-1" data-testid="materials-pending">
          {pending.map((p) => {
            const Icon = kindIcon(p.kind);
            const waiting = WAITING.has(p.status);
            return (
              <div key={p.id} data-testid={`materials-pending-${p.id}`}
                className="flex items-center gap-2 text-xs rounded-md border border-dashed border-page-divider px-2 py-1.5">
                <Icon size={12} className={`shrink-0 ${p.status === "failed" ? "text-red-600" : "text-page-muted"}`} />
                <span className="min-w-0 flex-1 truncate text-page-text">{p.name}</span>
                {waiting ? (
                  <span className="shrink-0 inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                    <Loader2 size={11} className="animate-spin" />
                    {p.status === "parsing" ? "reading" : "transcribing"}{p.progress ? ` ${p.progress}%` : "…"}
                  </span>
                ) : p.status === "failed" ? (
                  <span className="shrink-0 text-red-600 max-w-[50%] truncate" title={p.error || ""}>{p.error || "failed"}</span>
                ) : (
                  <span className="shrink-0 text-page-muted">{(p.chars || 0).toLocaleString()} chars</span>
                )}
              </div>
            );
          })}
          <div className="text-[11px] text-page-muted">
            The version is minted when every file has been read — one drop is one version, and one build card.
          </div>
        </div>
      )}

      {msg ? <div className="text-xs text-page-muted" data-testid="materials-msg">{msg}</div> : null}
      {err ? <div className="text-sm text-red-600" data-testid="materials-error">{err}</div> : null}

      {adding && (
        <div className="rounded-lg border border-page-divider p-3 space-y-2" data-testid="materials-add-form">
          <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="What is this request called?" data-testid="materials-add-name"
            className="w-full text-sm px-2 py-1.5 rounded-md border border-page-divider bg-card-bg text-page-text" />
          <textarea value={draft.text} onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
            rows={5} placeholder="Type the request in plain language — this becomes a source the requirements are derived from."
            data-testid="materials-add-text"
            className="w-full text-sm px-2 py-1.5 rounded-md border border-page-divider bg-card-bg text-page-text font-mono" />
          <div className="flex items-center gap-2">
            <button type="button" onClick={addRequest} disabled={busy} data-testid="materials-add-save"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-400 disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Add
            </button>
            <button type="button" onClick={() => setAdding(false)} className="text-xs text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer">Cancel</button>
          </div>
        </div>
      )}

      {/* The between-versions diff — which items appeared, changed, vanished. */}
      {diffTo != null && (
        <div className="rounded-lg border border-page-divider p-3 space-y-2" data-testid="materials-diff">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-page-muted">comparing v{diffTo} to</span>
            <select data-testid="materials-diff-base" value={diffFrom ?? ""} onChange={(e) => setDiffFrom(e.target.value === "" ? null : Number(e.target.value))}
              className="text-xs px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
              <option value="">v{diffTo - 1} (previous)</option>
              {versions.filter((v) => v.version !== diffTo).map((v) => (
                <option key={v.version} value={v.version}>v{v.version}</option>
              ))}
            </select>
            {diff ? <span className="text-page-muted ml-auto">{diff.summary}</span> : <Loader2 size={12} className="animate-spin" />}
          </div>
          {diff ? (
            <div className="space-y-1 text-xs">
              {diff.added.map((i) => (
                <div key={`a${i.id}`} className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300">
                  <span className="font-mono">+</span> {i.name} <span className="text-page-muted">({i.chars} chars)</span>
                </div>
              ))}
              {diff.changed.map((i) => (
                <div key={`c${i.id}`} className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                  <span className="font-mono">~</span> {i.name}
                  <span className="text-page-muted">({i.was_chars ?? "?"} → {i.chars} chars)</span>
                </div>
              ))}
              {diff.removed.map((i) => (
                <div key={`r${i.id}`} className="flex items-center gap-2 text-red-800 dark:text-red-300">
                  <span className="font-mono">−</span> {i.name}
                </div>
              ))}
              {!diff.added.length && !diff.changed.length && !diff.removed.length && (
                <div className="text-page-muted">No item changed between these versions.</div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {data == null && (
        <div className="flex items-center gap-2 text-sm text-page-muted"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      )}

      {data != null && items.length === 0 && !err && (
        <div className="rounded-lg border border-page-divider p-4 text-sm text-page-text space-y-2" data-testid="materials-empty">
          <div className="font-medium">No materials yet.</div>
          <div className="text-page-muted">
            Two ways in: <b>Attach files</b> — or drop them anywhere on this page
            ({supported || "documents, text and audio recordings"}) — and <b>Add a
            request</b> for something typed in plain language. Everything you add
            here is what the project's requirements get derived from.
          </div>
        </div>
      )}

      {/* The item list. Expanding one shows its full text — which is also where
          a feature's origin link lands, so the reader can see the quoted place. */}
      <div className="space-y-1">
        {items.map((i) => {
          const Icon = kindIcon(i.kind);
          const isOpen = open.has(i.id);
          return (
            <div key={i.id} data-testid={`material-${i.id}`} className="rounded-md border border-page-divider">
              <div className="flex items-center gap-2 px-2 py-1.5">
                <button type="button" data-testid={`material-toggle-${i.id}`} onClick={() => toggle(i.id)}
                  className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0">
                  {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                </button>
                <Icon size={13} className="text-page-muted shrink-0" />
                <span className="min-w-0 flex-1 truncate text-sm text-page-text" title={i.name}>{i.name}</span>
                <span className="shrink-0 text-[11px] text-page-muted">{i.kind}</span>
                <span className="shrink-0 text-[11px] text-page-muted">{i.chars} chars</span>
                {i.status && i.status !== "ready" ? (
                  <span className="shrink-0 text-[11px] text-amber-700 dark:text-amber-400">{i.status}…</span>
                ) : null}
                {i.has_original ? (
                  <button type="button" data-testid={`material-download-${i.id}`} disabled={dlBusy === i.id}
                    onClick={() => downloadOriginal(i)}
                    title="Download the original file"
                    className="shrink-0 text-page-muted hover:text-nav-active bg-transparent border-0 cursor-pointer p-0.5 disabled:opacity-50">
                    {dlBusy === i.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  </button>
                ) : null}
                {!viewing || viewing === cur ? (
                  <button type="button" data-testid={`material-retire-${i.id}`} disabled={busy}
                    onClick={() => editItem(i.id, { excluded: true }, `removed "${i.name}"`)}
                    title="Remove this source — a change is filed to the Board without it"
                    className="shrink-0 text-page-muted hover:text-red-700 dark:hover:text-red-400 bg-transparent border-0 cursor-pointer p-0.5 disabled:opacity-50">
                    <EyeOff size={12} />
                  </button>
                ) : null}
              </div>
              {isOpen ? (
                <div className="px-2 pb-2 pl-8">
                  {full[i.id] == null ? (
                    <div className="text-xs text-page-muted flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> reading…</div>
                  ) : (
                    <>
                      {i.kind === "image" && i.has_original ? <MaterialImage id={i.id} name={i.name} /> : null}
                      <MaterialBody id={i.id} name={i.name} text={full[i.id]}
                      coverage={!viewing || viewing === cur ? coverage[i.id] : null}
                      onCoverRest={!viewing || viewing === cur ? coverRest : null} covering={covering}
                      editable={!viewing || viewing === cur} busy={busy}
                      onOpenFeature={onOpenFeature} onOpenTask={onOpenTask}
                      onRequirementsChanged={() => { refreshCoverage(i.id); onJobsChange?.(); }}
                      onSave={(text) => editItem(i.id, { text }, `edited "${i.name}"`)} />
                    </>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// The assistant's answer about a selected passage: the verdict, the plain
// answer, the requirements that cover it and the rows it added — each a link
// into the Requirements tab.
const VERDICT_LABEL = {
  covered: { text: "already covered", tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
  partially_covered: { text: "partly covered — the rest was added", tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  gap: { text: "a gap — requirement(s) added", tone: "bg-sky-500/10 text-sky-700 dark:text-sky-400" },
  not_a_requirement: { text: "asks nothing of the project", tone: "bg-card-border/40 text-page-muted" },
};
function RequirementsAsk({ id, ask, onOpenFeature, onClose }) {
  const r = ask.result || {};
  const verdict = VERDICT_LABEL[r.verdict] || null;
  const rows = (list, testid) => (list || []).length ? (
    <span className="inline-flex flex-wrap gap-1" data-testid={testid}>
      {list.map((f) => (
        <button key={f.key} type="button" onClick={() => onOpenFeature?.(f.key)}
          className="text-[11px] px-1.5 py-0.5 rounded border border-page-divider bg-transparent cursor-pointer text-nav-active hover:underline">
          {f.name || f.key}
        </button>
      ))}
    </span>
  ) : null;
  return (
    <div data-testid={`material-req-ask-${id}`} data-state={ask.state}
      className="rounded-md border border-page-divider px-2.5 py-2 text-xs space-y-1.5" style={{ background: "var(--table-stripe)" }}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-page-muted">Requirements for the selected passage</div>
          <div className="text-[11px] text-page-muted italic truncate" title={ask.quote}>“{String(ask.quote || "").slice(0, 160)}{String(ask.quote || "").length > 160 ? "…" : ""}”</div>
          {ask.state === "booking" || ask.state === "running" ? (
            <div className="inline-flex items-center gap-1.5 text-page-muted" data-testid={`material-req-ask-running-${id}`}>
              <Loader2 size={11} className="animate-spin" />
              {ask.state === "booking" ? "Asking the assistant…" : `The assistant is checking the requirements and the implementation for this passage${ask.job_id ? ` (job ${ask.job_id})` : ""}…`}
            </div>
          ) : ask.state === "failed" ? (
            <div className="text-red-700 dark:text-red-400" data-testid={`material-req-ask-error-${id}`}>{ask.error}</div>
          ) : (
            <div className="space-y-1" data-testid={`material-req-ask-result-${id}`}>
              {verdict ? <span className={`inline-block text-[11px] px-1.5 py-0.5 rounded ${verdict.tone}`} data-verdict={r.verdict}>{verdict.text}</span> : null}
              {r.answer ? <div className="text-page-text">{r.answer}</div> : null}
              {(r.covered_rows || []).length ? <div className="flex flex-wrap items-center gap-1"><span className="text-page-muted">covered by</span> {rows(r.covered_rows, `material-req-ask-covered-${id}`)}</div> : null}
              {(r.added_rows || []).length ? <div className="flex flex-wrap items-center gap-1"><span className="text-page-muted">added</span> {rows(r.added_rows, `material-req-ask-added-${id}`)}</div> : null}
              {(r.reworked_rows || []).length ? <div className="flex flex-wrap items-center gap-1"><span className="text-page-muted">updated</span> {rows(r.reworked_rows, `material-req-ask-reworked-${id}`)}</div> : null}
            </div>
          )}
        </div>
        <button type="button" onClick={onClose} title="Close" data-testid={`material-req-ask-close-${id}`}
          className="shrink-0 text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0.5"><X size={12} /></button>
      </div>
    </div>
  );
}

// An IMAGE material — the approved screen mockup, a whiteboard photo — shown
// as the picture it is. The bytes come through the authenticated backend route
// (a presigned URL can name a host only the backend reaches): fetch → blob →
// object URL, revoked when the row folds.
function MaterialImage({ id, name }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let url = null; let alive = true;
    fetchData(`docs/materials/${id}/file`, { method: "GET", responseType: "blob" })
      .then((blob) => { if (!alive) return; url = URL.createObjectURL(blob); setSrc(url); })
      .catch((e) => { if (alive) setErr(String(e?.message || "could not load the picture")); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (err) return <div className="text-xs text-red-600 mb-2" data-testid={`material-image-error-${id}`}>{err}</div>;
  if (!src) return <div className="text-xs text-page-muted flex items-center gap-1.5 mb-2"><Loader2 size={11} className="animate-spin" /> loading the picture…</div>;
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Open the picture full size" className="block mb-2">
      <img src={src} alt={name} data-testid={`material-image-${id}`}
        className="max-w-full rounded-md border border-page-divider bg-white" style={{ maxHeight: 420 }} />
    </a>
  );
}

// One material's text, readable and (on the current version) editable. The
// `data-mat-pos-*` anchor is what a feature's origin link scrolls to.
function MaterialBody({ id, name, text, coverage, editable, busy, onSave, onOpenFeature, onOpenTask, onRequirementsChanged, onCoverRest, covering }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  // Hover popup over a covered phrase, and the select-to-ask chip. Both are
  // FIXED-positioned at pointer/selection coordinates, so the scrolling <pre>
  // never clips them.
  const [pop, setPop] = useState(null);   // { x, y, features }
  const [ask, setAsk] = useState(null);   // { x, y, text }
  // "Requirements for this?" — the assistant's answer about a selected passage
  // (2026-09-04): { state: booking|running|done|failed, quote, job_id, result, error }
  const [reqAsk, setReqAsk] = useState(null);
  const popTimer = useRef(null);
  useEffect(() => { setDraft(text); }, [text]);
  useEffect(() => () => clearTimeout(popTimer.current), []);

  // Slice the text into plain, COVERED and UNCOVERED runs. Segments and gaps
  // come start-sorted and non-overlapping from the backend; a gap is a run no
  // requirement links yet (whitespace and punctuation between quotes are
  // plain, not gaps). Once coverage is known, every gap is painted — that is
  // the rule made visible: nothing in a source may stay unlinked.
  const parts = useMemo(() => {
    const segs = coverage?.segments || [];
    const gaps = coverage?.gaps || [];
    if (!text || (!segs.length && !gaps.length)) return null;
    const marks = [
      ...segs.map((s) => ({ start: s.start, end: s.end, features: s.features })),
      ...gaps.map((g) => ({ start: g.start, end: g.end, gap: true })),
    ].sort((a, b) => a.start - b.start);
    const out = [];
    let at = 0;
    for (const m of marks) {
      if (m.start < at) continue;   // defensive: never paint twice
      if (m.start > at) out.push({ t: text.slice(at, m.start) });
      out.push({ t: text.slice(m.start, m.end), features: m.features, gap: m.gap });
      at = m.end;
    }
    if (at < text.length) out.push({ t: text.slice(at) });
    return out;
  }, [text, coverage]);

  const holdPop = () => clearTimeout(popTimer.current);
  const openPop = (e, features) => { holdPop(); setPop({ x: Math.min(e.clientX, window.innerWidth - 340), y: e.clientY + 14, features }); };
  const closePop = () => { holdPop(); popTimer.current = setTimeout(() => setPop(null), 250); };

  // Selecting ANY text (covered or not) offers "ask the orchestrator" — the
  // selection travels into the chat input as a quoted excerpt, never auto-sent.
  const onMouseUp = () => {
    try {
      const sel = window.getSelection();
      const chosen = String(sel || "").trim();
      const pre = document.getElementById(`material-body-${id}`);
      if (!chosen || chosen.length < 3 || !pre || !sel.anchorNode || !pre.contains(sel.anchorNode)) { setAsk(null); return; }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      // Clamped into the viewport: the chip is position:fixed, and a selection
      // whose end sits below the fold would otherwise put it off-screen.
      setAsk({
        x: Math.max(8, Math.min(rect.right, window.innerWidth - 220)),
        y: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - 40)),
        text: chosen.slice(0, 1200),
      });
    } catch { setAsk(null); }
  };
  const askOrchestrator = () => {
    window.dispatchEvent(new CustomEvent("cortex:open-harness", {
      detail: {
        harness: "orchestrator",
        initialPrompt: `From requirement source "${name}":\n\n"${ask.text}"\n\nQuestion: `,
      },
    }));
    setAsk(null);
    try { window.getSelection()?.removeAllRanges(); } catch { /* fine */ }
  };
  // ASK FOR THE REQUIREMENTS OF THIS PASSAGE. Not a chat: one scoped fleet
  // generation (POST /api/docs/features/from_selection) that answers whether
  // an existing requirement or the implementation already covers the passage,
  // and adds ONLY the missing rows. The answer lands on the review job;
  // review_status is polled until that job is done.
  const askRequirements = async () => {
    const quote = ask.text;
    setAsk(null);
    try { window.getSelection()?.removeAllRanges(); } catch { /* fine */ }
    setReqAsk({ state: "booking", quote });
    try {
      const r = await fetchData("docs/features/from_selection", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ material_id: id, quote }),
      });
      setReqAsk({ state: "running", quote, job_id: r?.job_id || null });
      onRequirementsChanged?.();
    } catch (e) {
      setReqAsk({ state: "failed", quote, error: String(e?.message || "The assistant could not be asked.") });
    }
  };
  useEffect(() => {
    if (!reqAsk || reqAsk.state !== "running" || !reqAsk.job_id) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const st = await fetchData("docs/review_status", { method: "GET" });
        const j = st?.job;
        if (!alive || !j || j.id !== reqAsk.job_id) return;
        if (j.status === "done") {
          setReqAsk((a) => ({ ...a, state: "done", result: j.result || {} }));
          onRequirementsChanged?.();
        } else if (j.status === "failed") {
          setReqAsk((a) => ({ ...a, state: "failed", error: st?.detail?.error || "the assistant did not answer" }));
        }
      } catch { /* keep polling */ }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqAsk?.state, reqAsk?.job_id]);
  if (editing) {
    return (
      <div className="space-y-1.5">
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={14}
          data-testid={`material-edit-${id}`}
          className="w-full text-xs px-2 py-1.5 rounded-md border border-page-divider bg-card-bg text-page-text font-mono" />
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { onSave(draft); setEditing(false); }} disabled={busy}
            data-testid={`material-save-${id}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-400 disabled:opacity-50">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Save — and file the change
          </button>
          <button type="button" onClick={() => { setDraft(text); setEditing(false); }}
            className="text-xs text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer">Cancel</button>
        </div>
      </div>
    );
  }
  // Linked share of the text: everything that is not an unlinked passage
  // counts (whitespace and punctuation between quotes are not text anyone
  // ignored), so a fully linked source reads 100%.
  const gaps = coverage?.gaps || [];
  const pct = coverage && coverage.total_chars
    ? Math.round(((coverage.total_chars - (coverage.uncovered_chars || 0)) / coverage.total_chars) * 100) : null;
  return (
    <div className="space-y-1">
      {pct != null ? (
        <div className="text-[10px] text-page-muted flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`material-coverage-${id}`}
          data-coverage-complete={coverage.complete ? "true" : "false"} data-coverage-gaps={gaps.length}>
          {coverage.segments.length
            ? <span>requirements link <b>{coverage.segments.length}</b> passage(s) — {pct}% of the text.</span>
            : <span>no requirement quotes this source yet.</span>}
          {coverage.complete
            ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"><Check size={10} /> every passage is linked to a requirement — nothing here was ignored.</span>
            : <>
                <span className="text-red-700 dark:text-red-400"><b>{gaps.length}</b> passage(s) are not linked to any requirement (red below).</span>
                {onCoverRest ? (
                  <button type="button" onClick={onCoverRest} disabled={covering} data-testid={`material-cover-rest-${id}`}
                    title="Book a coverage round: every unlinked passage is attached to a requirement, a new one, or filed as 'not a requirement' — nothing is left unread"
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50">
                    {covering ? <Loader2 size={10} className="animate-spin" /> : <ListChecks size={10} />} Cover the rest
                  </button>
                ) : null}
              </>}
        </div>
      ) : null}
      <pre id={`material-body-${id}`} data-testid={`material-body-${id}`} onMouseUp={onMouseUp}
        className="text-[11px] leading-relaxed whitespace-pre-wrap rounded-md p-2 max-h-[45vh] overflow-y-auto font-mono"
        style={{ background: "var(--table-stripe)" }}>
        {parts
          ? parts.map((seg, i) => seg.features ? (
              <mark key={i} data-testid={`material-covered-${id}-${i}`}
                onMouseEnter={(e) => openPop(e, seg.features)} onMouseLeave={closePop}
                className="rounded-sm px-0 cursor-help"
                style={{ background: "rgba(16,185,129,.18)", color: "inherit", boxShadow: "0 1px 0 rgba(16,185,129,.7)" }}>
                {seg.t}
              </mark>
            ) : seg.gap ? (
              <span key={i} data-testid={`material-uncovered-${id}-${i}`}
                title="Not linked to any requirement yet — select it and ask 'Requirements for this?', or press 'Cover the rest'"
                className="rounded-sm px-0"
                style={{ background: "rgba(239,68,68,.10)", textDecoration: "underline dashed rgba(239,68,68,.75)", textDecorationSkipInk: "none", textUnderlineOffset: "3px" }}>
                {seg.t}
              </span>
            ) : seg.t)
          : (text || "(no text)")}
      </pre>
      {pop ? (
        <div data-testid={`material-popup-${id}`} onMouseEnter={holdPop} onMouseLeave={closePop}
          className="fixed z-50 w-80 rounded-lg border border-page-divider bg-card-bg shadow-xl p-2 space-y-1.5 text-left"
          style={{ left: pop.x, top: pop.y }}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-page-muted">Requirements quoting this passage</div>
          {pop.features.map((f) => (
            <div key={f.key} className="space-y-0.5">
              <button type="button" onClick={() => { setPop(null); onOpenFeature?.(f.key); }}
                className="text-left text-xs text-nav-active bg-transparent border-0 p-0 cursor-pointer hover:underline">
                {f.name || f.key}
              </button>
              <span className="ml-1.5 text-[10px] text-page-muted">{f.kind === "noise" ? "not a requirement — this text asks for nothing" : `${f.status}${f.kind && f.kind !== "functional" ? ` · ${f.kind.replace("_", " ")}` : ""}`}</span>
              {(f.tasks || []).map((t) => (
                <div key={t.id} className="pl-2 text-[10px]">
                  <button type="button" onClick={() => { setPop(null); onOpenTask?.(t.id); }}
                    className="text-page-muted hover:text-nav-active bg-transparent border-0 p-0 cursor-pointer hover:underline">
                    {t.request_id ? `#${t.request_id} · ` : ""}{t.title}
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}
      {reqAsk ? <RequirementsAsk id={id} ask={reqAsk} onOpenFeature={onOpenFeature} onClose={() => setReqAsk(null)} /> : null}
      {ask ? (
        <div className="fixed z-50 flex items-stretch rounded-md border border-page-divider bg-card-bg shadow-lg overflow-hidden"
          style={{ left: Math.min(ask.x, window.innerWidth - 420), top: ask.y }} data-testid={`material-ask-chip-${id}`}>
          <button type="button" data-testid={`material-ask-reqs-${id}`} onClick={askRequirements}
            title="Ask the assistant whether a requirement (or the implementation) already covers this passage — and add what is missing"
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-transparent border-0 border-r border-page-divider cursor-pointer text-page-text hover:text-nav-active hover:bg-card-border/30">
            <ListChecks size={11} /> Requirements for this?
          </button>
          <button type="button" data-testid={`material-ask-${id}`} onClick={askOrchestrator}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-transparent border-0 cursor-pointer text-page-text hover:text-nav-active hover:bg-card-border/30">
            <MessagesSquare size={11} /> Ask the orchestrator about this
          </button>
        </div>
      ) : null}
      {editable ? (
        <button type="button" onClick={() => setEditing(true)} data-testid={`material-edit-open-${id}`}
          className="inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0">
          <Pencil size={10} /> edit
        </button>
      ) : null}
    </div>
  );
}
